import { IJourneyCopy } from './copyTypes';
import { DEFAULT_COPY } from './defaultCopy';

/**
 * CarExpert's wording, used only where a partner sets useCarExpertCopy.
 *
 * Spread from the default so anything not rewritten yet keeps today's words
 * rather than falling back to nothing.
 */
export const CAREXPERT_COPY: IJourneyCopy = {
    ...DEFAULT_COPY,
    quickCalculator: {
        // Hidden on the opening screen only; it returns from loan details on.
        showProgressBar: false,
        groupFields: true,
        darkEstimate: true,
        // The old slogan promised an instant response and made a credit-score
        // claim up front; the same reassurance now sits in the intro.
        sloganLines: [],
        heading: 'Car finance made easier',
        intro:
            'Start with an estimated repayment, then share a few more details ' +
            'to see personalised finance options and apply when you’re ready.',
        // Pulled out of the paragraph: it is the one line that answers the
        // question stopping people, so it should not be the tail of a
        // sentence about something else.
        reassurance: 'Won’t impact your credit score',
        // "Estimated Quote" above "You can finance this from only" said the
        // same thing twice, and neither is a quote.
        estimateLabel: undefined,
        estimateHeading: 'Estimated repayment',
        // The heading has no asterisk now, and the disclaimer says the same.
        showEstimateFootnote: false,
        // Dropping the label took the disclaimer icon with it, so it moves to
        // the heading. The modal behind it renders CarExpert's own five
        // sections from quickQuotePrivacyPolicy - the same content the review
        // asked to be shown.
        disclaimerOnHeading: true,
        cta: 'See personalised options',
        // The number itself comes from partner config.
        underCta: 'Prefer to talk? Call',
        underCtaPhone: true,
    },
    /*
     * Three steps rather than four, with the calculator's own step dropped:
     * the bar is hidden on that screen, so a step for it could never be the
     * active one and only made the remaining three narrower.
     *
     * The map still covers all four journey phases; the calculator shares the
     * first step so the highlight has somewhere to sit if the bar is ever
     * shown there.
     */
    progressSteps: ['Your details', 'Your quote', 'Apply to lender'],
    progressStepMap: [0, 0, 1, 2],
    screens: {
        loan: {
            title: 'About the car and your finance',
            subtitle:
                'Tell us a little more about what you’re looking for so we ' +
                'can tailor your finance options.',
        },
        business: {
            title: 'About your business',
            subtitle:
                'Tell us a few details about the business applying for finance.',
        },
        basicDetails: {
            title: 'About you',
            subtitle:
                'A few details about you will help us understand which ' +
                'finance options may be available.',
        },
        income: {
            title: 'Income and housing costs',
            subtitle:
                'Tell us about your regular income and housing costs so we ' +
                'can check which finance options may be available to you.',
        },
        identification: {
            title: 'Verify your identity',
            subtitle:
                'Upload your driver’s licence to pre-fill your details and ' +
                'save time. Or you can enter them manually.',
        },
        ocrSummary: {
            title: 'Check your details',
            subtitle:
                'Review what we’ve captured from your licence and edit ' +
                'anything that isn’t right.',
        },
        employment: {
            title: 'Employment history',
            subtitle:
                'Tell us about your employment over the past three years.',
        },
        assetsAndLiabilities: {
            title: 'Assets and financial commitments',
            subtitle:
                'Tell us about what you own and any existing debts or ' +
                'credit commitments.',
        },
        supportingDocs: {
            title: 'Supporting documents',
            subtitle:
                'Upload any documents your selected lender needs to ' +
                'complete your application.',
        },
    },
    fields: {
        ...DEFAULT_COPY.fields,
        // Short forms: the card groups these, so each label no longer has to
        // reintroduce the subject.
        purchasePrice: 'Purchase price',
        loanTerm: 'Loan term',
        loanTermMinUnit: 'yr',
        loanTermMaxUnit: 'yrs',
        vehicleCondition: 'Vehicle condition',
        creditHistory: 'Credit history',
        loanPurpose: "What's the finance for?",
        tradeIn: 'Trade-in value',
        deposit: 'Deposit amount',
        balloon: 'Balloon payment (%)',
        vehicleKnown: 'Do you know which {asset} you want?',
        vehicleKnownOptions: { yes: 'Yes', no: 'Not yet' },

        businessIndustry: 'Industry',
        abnLookup: 'Find your business using its ABN?',

        livingStatus: 'Your living situation',
        livingStatusOptions: {
            owner: 'Home owner',
            renter: 'Renter',
            boarding: 'Boarding / other',
        },
        marriageStatus: 'Relationship status',
        marriageStatusOptions: {
            married: 'Married / de facto',
            single: 'Single / divorced',
        },
        residencyStatusOptions: {
            citizen: 'Australian citizen',
            permanentResident: 'Permanent resident',
            visa: 'Visa holder',
        },
        // "Kids" asked a narrower question than lenders actually need.
        haveKids: 'Do you have financial dependants?',
        haveKidsOptions: { yes: 'Yes', no: 'No' },
        numberOfKids: 'Number of financial dependants',

        paidFrequency: 'How often are you paid?',
        housingFrequency: 'How often do you pay your rent or mortgage?',
        otherIncome: 'Other regular income',

        licenceFront: "Front of driver's licence",
        licenceBack: "Back of driver's licence",
        // "I'll do it later" implied the details could be skipped entirely.
        skipIdentification: 'Enter my details manually',

        assetCards: {
            realEstate: 'Property',
            motorVehicle: 'Vehicles',
            savings: 'Savings and investments',
            creditCards: 'Credit cards',
            personalLoans: 'Personal loans',
        },

        backButton: 'Back',
        // "Next" describes the button; "Continue" describes the journey.
        nextButton: 'Continue',

        submitApplication: 'Submit application',

        termsConsentLead: 'I have read & agreed to the',
        termsConsentLink: 'Terms & Privacy Policy',
        termsSmsMessage:
            'Hi {firstName}, thanks for exploring CarExpert Finance and accepting ' +
            'our Terms & Privacy Policy. You can review these anytime here: {link}.\n\n' +
            'You can continue online to compare finance options from over 40 bank ' +
            'and non-bank lenders, or if you\'d prefer to speak with someone, call ' +
            'us on {phone}. Our team is here to help you find a competitive rate ' +
            'and loan terms that suit your needs.',

        registerTitle: 'Your contact details',
        registerSubtitle: [
            'We’ll send a one-time code to verify your mobile and save your progress.',
        ],
    },
    // Replaces a checklist of six shouted steps, one of which announced that
    // the credit file was being fetched. What the customer needs to know is
    // that something is happening and roughly how long for.
    checkingApproval: {
        title: 'Checking your finance options',
        body: [
            'We’re checking your details against our lender panel to see which options may be available.',
            'This may take a moment.',
        ],
    },
    showScoresScreen: false,
    financeOptionCard: true,
    sentenceCaseAssetHeading: true,
    quietFallbackPrompt: true,
    leftAlignHeading: true,
    segmentedProgress: true,
    plainSubmittedModal: true,
    cardListRows: true,
    cardGround: true,
    segmentedOptions: true,
    referredTimeline: true,
    referredDetail: {
        intro: 'Thanks — we have your details.',
        body:
            'One of our finance specialists will review your application ' +
            'and contact you to discuss the next step.',
        steps: [
            {
                title: 'Details received',
                detail: 'No need to add anything else for now',
            },
            {
                title: 'Specialist review',
                detail: 'We’ll look over your application',
            },
            { title: 'We contact you', detail: 'To discuss the next step' },
        ],
        contactText: 'If you’d prefer to speak to us now, give us a call.',
        callCta: 'Call',
        backToStart: 'Back to start',
    },
    declinedCard: true,
    successCard: true,
    failureCard: true,
    technicalFailureScreen: true,
    technicalFailure: {
        title: 'We couldn’t complete the check',
        // The number sits between these two, from partner config.
        bodyPrefix:
            'Something went wrong while we were checking your details. ' +
            'Please try again, or call CarExpert on',
        bodySuffix: 'if you’d like some help.',
        cta: 'Try again',
        backToStart: 'Back to start',
    },
    contact: {
        // One line, without the colon: it sits above the details rather than
        // introducing a centred block.
        heading: 'Have a question? Get in touch',
        reduceLoan: 'Reduce finance amount',
        editAnswers: 'Review my details',
        editIncome: 'Edit income details',
    },
    submitLabelWhenSubmitting: true,
    declinedDetail: {
        contactText:
            'If you’d like to discuss the result or see whether there are ' +
            'other options available, contact CarExpert.',
        callCta: 'Call',
        backToStart: 'Back to start',
    },
    applicationSubmitted: {
        title: 'Application submitted',
        body: [
            'Your application has been sent to your selected lender.',
            'We’ll keep you updated and let you know if anything else is needed.',
        ],
        cta: 'Return to CarExpert',
    },
    financeOptions: {
        ...DEFAULT_COPY.financeOptions,
        title: 'Your finance options',
        body: [
            'Based on the information you’ve provided, these are the finance options currently available to you.',
            'Final rates and approval are subject to the lender’s assessment.',
        ],
        lowestBadge: 'Lowest repayment',
        cta: 'Select this option',
    },
    outcomes: {
        // "Great News!" implied an approval the customer has not been given.
        servicingSuccess: {
            title: 'Initial check complete',
            body: [
                'Based on the information you’ve provided, you can continue to ' +
                    'explore your finance options.',
                'We’ll need to confirm a few more details before showing the ' +
                    'options available to you. This isn’t final approval or an ' +
                    'offer of finance.',
            ],
        },
        servicingFailure: {
            title: 'Let’s review a few details',
            body: [
                'Based on the information entered, we haven’t been able to ' +
                    'match the amount you’re looking to borrow.',
                'You can check your income and housing costs or try a lower ' +
                    'finance amount.',
            ],
        },
        noIncome: {
            title: 'Regular income is needed',
            body: [
                'Every lender we work with needs to see a regular income before they can consider an application.',
                'That includes wages, and with some lenders, government payments such as a pension or disability support.',
                'If that has changed, or this was picked by mistake, you can go back and update your answer.',
            ],
        },
        referred: {
            title: 'We’ll review this with you',
            body: [
                'Thanks — we have your details.',
                'One of our finance specialists will review your application ' +
                    'and contact you to discuss the next step.',
            ],
        },
        declined: {
            title: 'We can’t offer an online finance option right now',
            body: [
                'We haven’t been able to match you with an online finance ' +
                    'option based on the information provided.',
            ],
        },
    },
};
